import type { Address } from 'viem';

export type Sym = 'SYN' | 'USDT' | 'BNB';

/** Swap path through PancakeSwap V2. BNB always routes through the WBNB/USDT pool (SYN is paired with USDT). */
export function pathFor(from: Sym, to: Sym, a: { syntra: Address; usdt: Address; wbnb: Address }): Address[] | null {
  if (from === to) return null;
  const addr = (s: Sym) => (s === 'SYN' ? a.syntra : s === 'USDT' ? a.usdt : a.wbnb);
  if (from === 'USDT' || to === 'USDT') return [addr(from), addr(to)];
  return [addr(from), a.usdt, addr(to)]; // SYN ↔ BNB via USDT
}

/** The other side of a deposit at the pool's current ratio (Uniswap V2 `quote`). */
export function matchAmount(amount: bigint, reserveIn: bigint, reserveOut: bigint): bigint {
  if (reserveIn === 0n) return 0n;
  return (amount * reserveOut) / reserveIn;
}

/** LP tokens minted for a deposit into an existing pool. */
export function lpForDeposit(a: bigint, b: bigint, ra: bigint, rb: bigint, supply: bigint): bigint {
  if (ra === 0n || rb === 0n || supply === 0n) return 0n;
  const x = (a * supply) / ra;
  const y = (b * supply) / rb;
  return x < y ? x : y;
}

/** Tokens returned for burning `lp`. */
export function amountsForLp(lp: bigint, ra: bigint, rb: bigint, supply: bigint): [bigint, bigint] {
  if (supply === 0n) return [0n, 0n];
  return [(lp * ra) / supply, (lp * rb) / supply];
}

/**
 * Price impact of a quote: how much worse the trade's average rate is than the marginal rate (a tiny trade on the
 * same route, which also pays the pool fee, so the fee is not counted as impact).
 */
export function priceImpact(amountIn: bigint, amountOut: bigint, smallIn: bigint, smallOut: bigint): number {
  if (amountIn === 0n || smallIn === 0n || smallOut === 0n) return 0;
  const rate = Number(amountOut) / Number(amountIn);
  const marginal = Number(smallOut) / Number(smallIn);
  return Math.max(0, 1 - rate / marginal);
}

export function withSlippage(amount: bigint, bps: bigint): bigint {
  return (amount * (10_000n - bps)) / 10_000n;
}
